// The analytics dashboard. Reached at /dashboard; any signed-in Crown user may open it.
//
// WHAT IT IS FOR. `usage_event` is written on every turn, and until this page nothing read
// it back. Each panel is chosen to answer a question someone would act on: which of
// Crown's policies actually answer questions, which questions come back with no source,
// and every thumbs-down with the question that earned it. Volume is the least interesting
// number here and is deliberately not the headline.
//
// THREE THINGS THE FIRST VERSION GOT WRONG, all reported after one look at real data:
//
//  1. PANELS GREW WITHOUT BOUND. Every list rendered every row, so "Turns per day" on a
//     30-day window was thirty rows — twenty-eight of them zero — and it pushed every
//     other panel off the screen. Lists are now capped at a few rows with an explicit
//     "show all N", and the time series is bucketed weekly by the SQL for long windows.
//  2. NOTHING WAS CLICKABLE. Every number was a dead end: you could see that the Symetra
//     Hospital Policy answered three questions and had no way to find out which three.
//     Every row now opens the questions behind it.
//  3. THE WINDOW WAS THREE BUTTONS. A dropdown, with the panel-level filters beside it.
//
// NO CHART LIBRARY. A bar is a div with a width. Nine panels of a few dozen rows do not
// justify ~100kB in a bundle served from the same origin as the API.
//
// STYLING USES THE EXISTING TOKENS ONLY. `npm run test:theme` asserts every var() names a
// token that exists and that every text/background pair clears WCAG AA in both themes. An
// undefined custom property renders transparent rather than erroring.

import { useCallback, useEffect, useState } from "react";
import { authHeaders } from "./api/client";
import { clientName } from "./auth/msal";

interface Overview {
  window: number;
  data: Record<string, unknown>;
  failed?: string[];
}

/** What a click opened: the filter to send, and how to title the drawer. */
interface Drill {
  title: string;
  filters: Record<string, string>;
}

const WINDOWS = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
  { days: 365, label: "Last year" },
] as const;

/** How many rows a list shows before "show all". Small on purpose: the top of a ranked
 *  list is the finding, and the tail is available on request. */
const PREVIEW_ROWS = 5;

const num = (v: unknown): number => (typeof v === "number" ? v : Number(v ?? 0) || 0);
const pct = (part: unknown, whole: unknown): string => {
  const w = num(whole);
  if (!w) return "—";
  return `${Math.round((num(part) / w) * 100)}%`;
};
const rows = (d: unknown): Record<string, unknown>[] => (Array.isArray(d) ? (d as Record<string, unknown>[]) : []);
const one = (d: unknown): Record<string, unknown> => (d && typeof d === "object" && !Array.isArray(d) ? (d as Record<string, unknown>) : {});
const str = (v: unknown, dflt = "—"): string => (v === null || v === undefined || v === "" ? dflt : String(v));
const when = (v: unknown): string => {
  if (!v) return "—";
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};
/** A bucket label. Weekly buckets say "week of", because "Sep 8" alone reads as one day. */
const period = (v: unknown, grain: unknown): string => {
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return grain === "week" ? `week of ${day}` : day;
};

function Stat({ label, value, prev, hint, inverse, onClick }: {
  label: string;
  value: number;
  prev?: number;
  hint?: string;
  inverse?: boolean;
  onClick?: () => void;
}) {
  const delta = prev === undefined || prev === 0 ? null : Math.round(((value - prev) / prev) * 100);
  const good = delta === null ? null : inverse ? delta <= 0 : delta >= 0;
  const body = (
    <>
      <div className="dash-stat-label">{label}</div>
      <div className="dash-stat-value">{value.toLocaleString()}</div>
      {delta !== null && (
        <div className={`dash-stat-delta ${good ? "up" : "down"}`}>
          {delta > 0 ? "↑" : delta < 0 ? "↓" : "→"} {Math.abs(delta)}% vs previous
        </div>
      )}
      {hint && <div className="dash-stat-hint">{hint}</div>}
    </>
  );
  // A button only when there is something behind it. A tile that looks clickable and
  // does nothing is worse than a plain one.
  return onClick ? (
    <button type="button" className="dash-stat is-clickable" onClick={onClick}>
      {body}
    </button>
  ) : (
    <div className="dash-stat">{body}</div>
  );
}

function Bars({ data, labelKey, valueKey, sub, onPick, labelFmt }: {
  data: Record<string, unknown>[];
  labelKey: string;
  valueKey: string;
  sub?: (r: Record<string, unknown>) => string;
  onPick?: (r: Record<string, unknown>) => void;
  /** Format the row label. Without it the raw column is rendered, which is why the time
   *  series first shipped reading "2026-08-12T00:00:00.000Z" down the side of a panel. */
  labelFmt?: (r: Record<string, unknown>) => string;
}) {
  const [all, setAll] = useState(false);
  if (!data.length) return <p className="dash-empty">Nothing recorded in this window.</p>;
  // Scaled against the WHOLE set, not the visible slice, so expanding the list does not
  // silently rescale every bar and change what the shape appears to say.
  const max = Math.max(...data.map((r) => num(r[valueKey])), 1);
  const shown = all ? data : data.slice(0, PREVIEW_ROWS);
  return (
    <>
      <ul className="dash-bars">
        {shown.map((r, i) => {
          const label = labelFmt ? labelFmt(r) : str(r[labelKey]);
          const inner = (
            <>
              <div className="dash-bar-head">
                <span className="dash-bar-label" title={label}>
                  {label}
                </span>
                <span className="dash-bar-value">{num(r[valueKey]).toLocaleString()}</span>
              </div>
              <div className="dash-bar-track">
                <div className="dash-bar-fill" style={{ width: `${(num(r[valueKey]) / max) * 100}%` }} />
              </div>
              {sub && <div className="dash-bar-sub">{sub(r)}</div>}
            </>
          );
          return (
            <li key={i}>
              {onPick ? (
                <button type="button" className="dash-bar-btn" onClick={() => onPick(r)}>
                  {inner}
                </button>
              ) : (
                inner
              )}
            </li>
          );
        })}
      </ul>
      {data.length > PREVIEW_ROWS && (
        <button type="button" className="dash-more" onClick={() => setAll(!all)}>
          {all ? "Show less" : `Show all ${data.length}`}
        </button>
      )}
    </>
  );
}

function Panel({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section className="dash-panel">
      <h2>{title}</h2>
      {note && <p className="dash-note">{note}</p>}
      {children}
    </section>
  );
}

/** The drill-down. Fetches the filtered question list for whatever was clicked.
 *
 *  Mounted only while open, so the fetch happens on the click rather than on page load —
 *  these return up to 200 rows of question text and have no business in the payload of
 *  every visit. */
function Drawer({ drill, days, onClose }: { drill: Drill; days: number; onClose: () => void }) {
  const [data, setData] = useState<Record<string, unknown>[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const qs = new URLSearchParams({ window: String(days), ...drill.filters });
        const res = await fetch(`/api/analytics/panel/questions?${qs}`, { headers: await authHeaders() });
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        const body = (await res.json()) as { data: Record<string, unknown>[] };
        if (!cancelled) setData(body.data ?? []);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Couldn't load these questions.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [drill, days]);

  return (
    <div className="dash-scrim" role="dialog" aria-modal="true" aria-label={drill.title} onClick={onClose}>
      <div className="dash-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="dash-drawer-head">
          <h2>{drill.title}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        {err && <div className="dash-error">{err}</div>}
        {!data && !err && <p className="dash-empty">Loading…</p>}
        {data && data.length === 0 && <p className="dash-empty">No questions match this in the current window.</p>}
        {data && data.length > 0 && (
          <table className="dash-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Question</th>
                <th>Sources</th>
                <th>Documents</th>
              </tr>
            </thead>
            <tbody>
              {data.map((x, i) => (
                <tr key={i}>
                  <td>{when(x.asked_at)}</td>
                  <td>{str(x.person)}</td>
                  <td>
                    {str(x.question, "(aged out under the retention policy)")}
                    {x.feedback === "down" && <span className="dash-flag"> 👎 {str(x.comment, "")}</span>}
                    {x.declined === true && <span className="dash-flag"> couldn&apos;t help</span>}
                  </td>
                  <td>{num(x.sources) || "none"}</td>
                  <td>{Array.isArray(x.documents) ? (x.documents as string[]).join(", ") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [days, setDays] = useState<number>(30);
  const [ov, setOv] = useState<Overview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState<Drill | null>(null);

  const load = useCallback(async (w: number) => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch(`/api/analytics/overview?window=${w}`, { headers: await authHeaders() });
      if (!res.ok) {
        throw new Error(
          res.status === 503
            ? "Analytics needs the conversation store, which isn't reachable."
            : res.status === 401
              ? "Your session isn't authorised for analytics. Try reloading."
              : `Request failed (${res.status})`,
        );
      }
      setOv((await res.json()) as Overview);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't load analytics.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(days);
  }, [days, load]);

  const d = ov?.data ?? {};
  const s = one(d.summary);
  const p = one(d.previous);
  const r = one(d.retrieval);

  return (
    <main className="dash">
      <header className="dash-head">
        <div>
          <h1>{clientName()} HR Assistant — usage</h1>
          <p className="dash-sub">
            Every turn is recorded: what was asked, which documents answered it, and how it went. Click any row to read
            the questions behind it. Questions and who asked them are visible to everyone on the team.
          </p>
        </div>
        <div className="dash-controls">
          <label className="dash-select">
            <span className="sr-only">Time window</span>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {WINDOWS.map((w) => (
                <option key={w.days} value={w.days}>
                  {w.label}
                </option>
              ))}
            </select>
          </label>
          <a className="btn" href="/">
            Back to chat
          </a>
        </div>
      </header>

      {err && <div className="dash-error">{err}</div>}
      {loading && !ov && <p className="dash-empty">Loading…</p>}
      {ov?.failed?.length ? (
        // Named rather than silently absent: a missing panel otherwise reads as "no
        // data" when it actually means "this query broke".
        <div className="dash-error">These panels failed to load: {ov.failed.join(", ")}</div>
      ) : null}

      {ov && (
        <>
          <div className="dash-stats">
            <Stat label="Questions asked" value={num(s.questions)} prev={num(p.questions)} onClick={() => setDrill({ title: "Every question asked", filters: {} })} />
            <Stat label="People using it" value={num(s.people)} prev={num(p.people)} />
            <Stat
              label="Answered with no source"
              value={num(s.ungrounded)}
              hint={`${pct(s.ungrounded, s.questions)} of questions — the number to drive down`}
              inverse
              onClick={() => setDrill({ title: "Questions answered with no source", filters: { outcome: "ungrounded" } })}
            />
            <Stat
              label="Couldn't help"
              value={num(s.declined)}
              prev={num(p.declined)}
              inverse
              onClick={() => setDrill({ title: "Questions it couldn't help with", filters: { outcome: "declined" } })}
            />
            <Stat
              label="Thumbs down"
              value={num(s.thumbs_down)}
              hint={`${num(s.thumbs_up)} up`}
              inverse
              onClick={() => setDrill({ title: "Answers rated thumbs-down", filters: { outcome: "thumbs_down" } })}
            />
            <Stat label="Typical reply" value={num(s.avg_latency_ms)} hint={`p95 ${num(s.p95_latency_ms).toLocaleString()}ms`} />
          </div>

          <div className="dash-grid">
            <Panel
              title="Documents that answer questions"
              note="Which of Crown's policies are load-bearing. Absences matter as much as the top of the list. Rows prefixed “FAQ:” are internal FAQ entries rather than policy documents — a high one is a candidate for promoting into a real policy."
            >
              <Bars
                data={rows(d.documents)}
                labelKey="document"
                valueKey="citations"
                sub={(x) => `${num(x.people)} people · last ${when(x.last_cited)}`}
                onPick={(x) => setDrill({ title: `Questions answered from “${str(x.document)}”`, filters: { doc: str(x.document, "") } })}
              />
            </Panel>

            <Panel title="Questions with no source" note="Retrieval found nothing usable, or the answer cited nothing. The highest-value list here — each row is a gap in the synced corpus.">
              <Bars
                data={rows(d.gaps)}
                labelKey="topic"
                valueKey="turns"
                sub={(x) => `${num(x.declined)} declined · ${str(x.avg_passages, "0")} passages retrieved on average`}
                onPick={(x) => setDrill({ title: `Ungrounded questions about ${str(x.topic)}`, filters: { topic: str(x.topic, ""), outcome: "ungrounded" } })}
              />
            </Panel>

            <Panel
              title="Most-asked questions"
              note="Paraphrases collapsed into one row, so “how does jury duty work” and “what is our jury duty policy” count together. Asks against grounded is the number that names a missing document."
            >
              <Bars
                data={rows(d.questionGroups)}
                labelKey="question"
                valueKey="asks"
                sub={(x) => {
                  const asks = num(x.asks);
                  const grounded = num(x.grounded);
                  const variants = num(x.variants);
                  return [
                    `${grounded} of ${asks} grounded`,
                    variants > 1 ? `${variants} phrasings` : null,
                    `last ${when(x.last_asked)}`,
                  ]
                    .filter(Boolean)
                    .join(" · ");
                }}
                onPick={(x) => setDrill({ title: str(x.question), filters: { group: str(x.group_key, "") } })}
              />
            </Panel>

            <Panel title="Topics" note="What people ask about, against a fixed list of ten. A rising “other” means the list needs a new category, not that the classifier broke.">
              <Bars
                data={rows(d.topics)}
                labelKey="topic"
                valueKey="turns"
                sub={(x) => `${num(x.ungrounded)} with no source`}
                onPick={(x) => setDrill({ title: `Questions about ${str(x.topic)}`, filters: { topic: str(x.topic, "") } })}
              />
            </Panel>

            <Panel title="Usage over time" note="Bucketed weekly beyond a fortnight, because a row per day is a table rather than a chart.">
              <Bars
                data={rows(d.volume)}
                labelKey="period"
                valueKey="turns"
                labelFmt={(x) => period(x.period, x.grain)}
                sub={(x) => `${num(x.questions)} questions · ${num(x.people)} people · ${num(x.declined)} declined`}
              />
            </Panel>

            <Panel title="What people do" note="Ask a policy question, draft a reply, revise one.">
              <Bars
                data={rows(d.outcomes)}
                labelKey="intent"
                valueKey="turns"
                sub={(x) => `${num(x.grounded)} grounded · ${str(x.avg_sources, "0")} sources on average`}
                onPick={(x) => setDrill({ title: `“${str(x.intent)}” turns`, filters: { intent: str(x.intent, "") } })}
              />
            </Panel>

            <Panel title="Who's using it">
              <Bars
                data={rows(d.people)}
                labelKey="person"
                valueKey="turns"
                sub={(x) => `${num(x.grounded)} grounded · ${num(x.declined)} declined · last ${when(x.last_seen)}`}
                onPick={(x) => setDrill({ title: `Questions from ${str(x.person)}`, filters: { person: str(x.person, "") } })}
              />
            </Panel>

            <Panel title="Busiest hours" note="Central time, matching the Benefits Service Center hours in the reply template.">
              <Bars
                data={rows(d.hours)}
                labelKey="hour"
                valueKey="turns"
                labelFmt={(x) => {
                  // A bare "14" is a column value, not a time of day.
                  const h = num(x.hour);
                  const am = h < 12;
                  const twelve = h % 12 === 0 ? 12 : h % 12;
                  return `${twelve}${am ? "am" : "pm"}`;
                }}
              />
            </Panel>

            <Panel title="Retrieval health" note="Retrieved-but-uncited rising while volume is flat means the corpus stopped covering what people ask.">
              <dl className="dash-facts">
                <dt>Passages retrieved per question</dt>
                <dd>{str(r.avg_passages)}</dd>
                <dt>Sources cited per question</dt>
                <dd>{str(r.avg_cited)}</dd>
                <dt>Retrieved but cited nothing</dt>
                <dd>{num(r.retrieved_but_uncited).toLocaleString()}</dd>
                <dt>Found nothing at all</dt>
                <dd>{num(r.no_passages).toLocaleString()}</dd>
                <dt>Cited two or more documents</dt>
                <dd>{num(r.multi_source).toLocaleString()}</dd>
              </dl>
            </Panel>
          </div>

          <Panel title="Ratings" note="A thumbs-down next to the sources tells you whether the fault was retrieval or the answer. Those have different fixes.">
            {rows(d.feedback).length === 0 ? (
              <p className="dash-empty">No ratings in this window.</p>
            ) : (
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Who</th>
                    <th>Rating</th>
                    <th>Question</th>
                    <th>Comment</th>
                    <th>Sources</th>
                  </tr>
                </thead>
                <tbody>
                  {rows(d.feedback).map((x, i) => (
                    <tr key={i}>
                      <td>{when(x.rated_at)}</td>
                      <td>{str(x.person)}</td>
                      <td>{x.rating === "down" ? "👎" : "👍"}</td>
                      <td>{str(x.question)}</td>
                      <td>{str(x.comment, "")}</td>
                      <td>{num(x.sources)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </>
      )}

      {drill && <Drawer drill={drill} days={days} onClose={() => setDrill(null)} />}
    </main>
  );
}
